import { Checkbox, Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { Calendar, MarkerPin_01, PhoneCall_01, VideoRecorder } from '@recruit-robin/robin-theme/web-icons';

import { useDictionary } from '@/shared/lib/i18n';
import { useStyles } from './AutoSendInterviewInvite.styles';
import { InterviewType } from '../../model';
import { useNylasCalendarStore } from '../../lib';
import { useModal as useNylasCalendarManageConnectionModal } from '../use-modal';

const InterviewTypePill = ({
  interviewType,
  onClick,
}: {
  interviewType: InterviewType;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  if (interviewType === InterviewType.Online) {
    return (
      <Pill
        customCss={classes.autoSendInviteInterviewType}
        onClick={onClick}
        style="icon-removable"
        type="subtle2"
        startIcon={VideoRecorder}
        label={dictionary.onlineInterview}
        size="small"
      />
    );
  }
  if (interviewType === InterviewType.InPerson) {
    return (
      <Pill
        customCss={classes.autoSendInviteInterviewType}
        onClick={onClick}
        style="icon-removable"
        type="subtle2"
        startIcon={MarkerPin_01}
        label={dictionary.inPersonInterview}
        size="small"
      />
    );
  }
  if (interviewType === InterviewType.Call) {
    return (
      <Pill
        customCss={classes.autoSendInviteInterviewType}
        onClick={onClick}
        style="icon-removable"
        type="subtle2"
        startIcon={PhoneCall_01}
        label={dictionary.callInterview}
        size="small"
      />
    );
  }

  return null;
};

export const AutoSendInterviewInvite = ({
  isAutoSendInterviewInviteEnabled,
  onToggle,
}: {
  isAutoSendInterviewInviteEnabled: boolean;
  onToggle: () => void;
}) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  const interviewType = useNylasCalendarStore((s) => s.interviewType);

  const nylasCalendar = useNylasCalendarManageConnectionModal({
    initialInterviewType: interviewType,
    skipManageConnection: true,
    onFinish: () => nylasCalendar.close(),
    onCancel: () => nylasCalendar.close(),
  });

  return (
    <>
      <div
        css={[
          classes.autoSendInviteCard,
          isAutoSendInterviewInviteEnabled ? classes.autoSendInviteCardEnabled : classes.autoSendInviteCardDisabled,
        ]}
        onClick={onToggle}>
        <div css={classes.autoSendInviteCheckbox}>
          <Checkbox checked={isAutoSendInterviewInviteEnabled} />
        </div>
        <div css={classes.autoSendInviteContent}>
          <div css={classes.autoSendInviteHeader}>
            <Typography
              variant={(v) => v.body.long}
              color={(c) => (isAutoSendInterviewInviteEnabled ? c.neutral.variant[80] : c.neutral.variant[60])}
              inline
              ellipsis>
              {dictionary.autoSendInterviewInviteTitle}
            </Typography>

            <Calendar
              size={20}
              color={(c) => (isAutoSendInterviewInviteEnabled ? c.neutral.variant[80] : c.neutral.variant[60])}
            />

            <InterviewTypePill
              interviewType={interviewType}
              onClick={(e) => [e.stopPropagation(), nylasCalendar.open()]}
            />
          </div>

          <Typography
            variant={(v) => v.body.short}
            color={(c) => (isAutoSendInterviewInviteEnabled ? c.neutral.variant[70] : c.neutral.variant[60])}>
            {dictionary.autoSendInterviewInvite}
          </Typography>
        </div>
      </div>

      {nylasCalendar.modal}
    </>
  );
};
